import { useLazyQuery } from '@apollo/client/react';
import type { FC, PropsWithChildren } from 'react';
import { useAsync, useLocalStorage } from 'react-use';

import { GetMembersDocument, type GetMembersQueryResult } from '@zen/Auth/graphql';
import useAuth from '@zen/Auth/hooks/useAuth';
import useApiAuthoriser from '@zen/Auth/RouteAuthenticator/useApiAuthoriser';
import { SplashScreen } from '@zen/DesignSystem';

import AccountIdContext from './AccountIdContext';
import { OrganizationalRoleEnum } from './types';

const AccountIdProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const ACCOUNT_UUID_KEY = 'current_account_uuid';
  const [accountId, setAccountId, removeAccountId] = useLocalStorage<string>(ACCOUNT_UUID_KEY, '');
  const { logout } = useAuth();
  const [getMembers] = useLazyQuery<GetMembersQueryResult>(GetMembersDocument);
  const { hasApiAccessToken } = useApiAuthoriser();

  const fetchAccountId = async (): Promise<string> => {
    const { data } = await getMembers();
    const account = data?.currentUserDetails?.account;

    if (!account) {
      return '';
    }

    const isEligibleRole =
      account.organizationalRole === OrganizationalRoleEnum.CONSIGNMENT_PARTY ||
      account.organizationalRole === OrganizationalRoleEnum.AGENT;

    if (!isEligibleRole) {
      return '';
    }

    if (account.organizationalRole === OrganizationalRoleEnum.AGENT && account.parentAccountId != null) {
      return account.parentAccountId;
    }

    return account.id;
  };

  const initialiseAccountId = async () => {
    if (accountId) {
      return;
    }

    const id: string = await fetchAccountId();

    setAccountId(id);
  };

  const { loading, error } = useAsync(async () => {
    if (hasApiAccessToken) {
      initialiseAccountId();
    }
  }, [hasApiAccessToken]);

  if (loading) {
    return <SplashScreen />;
  }

  if (error) {
    logout();

    return null;
  }

  return (
    <AccountIdContext.Provider value={{ accountId: accountId || '', setAccountId, removeAccountId, fetchAccountId }}>
      {children}
    </AccountIdContext.Provider>
  );
};

export default AccountIdProvider;
